import React, { useEffect, useState } from 'react';
import { getTopRatedDoctors } from '../services/ratingsApi';
import { getDepartments } from '../services/doctorsApi';
import { useNavigate } from 'react-router-dom';
import { DoctorCard, SectionHeader } from './ui';

/* ── Skeleton card ───────────────────────────────────────────────── */
const SkeletonDoctorCard = () => (
  <div className="ds-doc">
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div className="skeleton" style={{ width: 72, height: 72, borderRadius: 14, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="skeleton skeleton-line" style={{ width: '65%', height: 13, marginBottom: 7 }} />
        <div className="skeleton skeleton-line" style={{ width: '45%', height: 11, marginBottom: 7 }} />
        <div className="skeleton skeleton-line" style={{ width: '55%', height: 10 }} />
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div className="skeleton skeleton-line" style={{ width: '50%', height: 11 }} />
      <div className="skeleton" style={{ width: 90, height: 34, borderRadius: 10 }} />
    </div>
  </div>
);

/* ── Main exported section ───────────────────────────────────────── */

/**
 * `limit` is how many cards to show — top N overall for "All Departments",
 * or top N *within* whichever department chip is selected. `poolSize` is
 * how many rated doctors to fetch up front so every department is fully
 * represented before we group/slice — the /top-rated endpoint only takes
 * an overall `limit`, not a per-department one, so grouping is done here.
 */
const TopRatedDoctors = ({ limit = 3, poolSize = 500 }) => {
  const navigate = useNavigate();
  const [doctors, setDoctors]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState('all');

  useEffect(() => {
    getTopRatedDoctors(poolSize)
      .then(data => { setDoctors(data); setLoading(false); })
      .catch(() => { setDoctors([]); setLoading(false); });
  }, [poolSize]);

  useEffect(() => {
    getDepartments().then(setDepartments).catch(() => {});
  }, []);

  // Hide section entirely if no data and not loading
  if (!loading && doctors.length === 0) return null;

  const handleBook = (doctor) => {
    navigate(`/services/doctors/${doctor.id}/book?mode=online`);
  };

  const handleView = (doctor) => {
    navigate(`/services/doctors/${doctor.id}/profile`);
  };

  // Only surface department chips that actually have a top-rated doctor in
  // the fetched pool, so picking a chip never lands on a dead "0 results" state.
  const availableDeptIds = new Set(doctors.map(d => d.department));
  const visibleDepartments = departments.filter(
    d => d.id === 'all' || availableDeptIds.has(d.id)
  );

  // Backend already sorts the pool by rating DESC, reviewCount DESC, so
  // top-N-per-department is just "first N matching that department".
  const filteredDoctors = (selectedDept === 'all'
    ? doctors
    : doctors.filter(d => d.department === selectedDept)
  ).slice(0, limit);

  return (
    <div>
      <SectionHeader
        title="Top Rated Doctors"
        subtitle="Verified, experienced doctors available now"
        onViewAll={() => navigate('/services/doctors')}
      />

      {/* Department filter */}
      {!loading && visibleDepartments.length > 1 && (
        <div className="chip-row">
          {visibleDepartments.map(d => (
            <button
              key={d.id}
              className={`chip${selectedDept === d.id ? ' active' : ''}`}
              onClick={() => setSelectedDept(d.id)}
            >
              <span>{d.icon}</span> {d.label}
            </button>
          ))}
        </div>
      )}

      {/* Cards grid */}
      <div className="ds-card-grid">
        {loading
          ? Array.from({ length: limit }).map((_, i) => <SkeletonDoctorCard key={i} />)
          : filteredDoctors.length === 0
            ? (
              <div style={{
                gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0',
                color: 'var(--ds-text-muted)', fontSize: 13,
              }}>
                No top-rated doctors in this department yet.
              </div>
            )
            : filteredDoctors.map((doc, i) => (
              <div key={doc.id}>
                <DoctorCard
                  doctor={{
                    ...doc,
                    departmentLabel: doc.departmentLabel || doc.department || 'General Medicine',
                    fee: doc.onlineFee ?? doc.fee,
                  }}
                  featured={i === 0}
                  clickable
                  location={doc.address ? doc.address.split(',')[0] : null}
                  onView={() => handleView(doc)}
                  onBook={() => handleBook(doc)}
                />
              </div>
            ))
        }
      </div>
    </div>
  );
};

export default TopRatedDoctors;